/* local-v3.js — behaviour for the v3 local (town) page template. No dependencies, ~4 KB.
   1. Intro "Read more" (the full paragraph is always in the HTML; the clamp is presentational).
   2. Occasion tabs: fetch a collection fragment through the Section Rendering API on click, cache it, swap the grid.
   3. Product-link attribution: hrefs stay clean in the HTML; on user intent (pointer/keyboard) the legacy
      florist_city / florist_state / utm_campaign / utm_source parameters (kept for the shared attribution scripts) are appended so product pages, the
      breadcrumb (utm-handler.js) and Ads attribution keep working exactly as before. Crawlers never see the parameters.
   4. Map facade: the Google Maps Embed iframe is created only when "Open interactive map" is clicked.
   5. Sub-category chips (28 Sep 2026, as on the James Cress town pages): a <template data-lv3-subs-for="{tab handle}"> in a products
      block holds a chip nav for one tab; it is cloned into the panel when that tab is chosen and removed for the others, and a chip
      loads its collection through the same fragment. Nothing renders until the tap, so the first screen never moves.
   6. Show more (28 and 29 Sep 2026): each tap loads the next batch (nothing loads before the tap), shows placeholders at once
      and swaps in the cards; the button counts what the next tap shows until the category runs out. */
(function () {
  'use strict';
  var d = document;

  function readJSON(sel) {
    var el = d.querySelector(sel);
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }
  var ctx = readJSON('[data-lv3-context]') || {};

  /* 1. Read more (28 Sep 2026, Nick: like the James Cress town pages). Inline placement: the button lives inside the intro box,
     first in the source while the text is clamped (two right floats put it at the end of line 2) and last once expanded, so
     Read less follows the text. The delivery facts (.lv3-ribbon--fold) open and close with it through data-lv3-state. */
  var intro = d.querySelector('[data-lv3-intro]');
  var btn = d.querySelector('[data-lv3-readmore]');
  var hero = d.querySelector('[data-lv3-hero]');
  if (intro && btn) {
    var inline = intro.contains(btn);
    if (inline) {
      var lh = parseFloat(window.getComputedStyle(intro).lineHeight);
      if (lh > 0) intro.style.setProperty('--lv3-lh', lh + 'px');
    }
    var clamped = intro.scrollHeight > intro.clientHeight + 4;
    var folded = !!(hero && hero.classList.contains('lv3-hero--fold'));
    var place = function (open) {
      if (!inline) return;
      if (open || !clamped) intro.appendChild(btn); else intro.insertBefore(btn, intro.firstChild);
    };
    if (!clamped) intro.classList.remove('lv3-clamp');
    place(false);
    if (clamped || folded) {
      btn.hidden = false;
      btn.addEventListener('click', function () {
        var open = btn.getAttribute('aria-expanded') !== 'true';
        intro.classList.toggle('lv3-clamp', !open && clamped);
        place(open);
        if (hero) hero.setAttribute('data-lv3-state', open ? 'open' : 'closed');
        btn.textContent = open ? (btn.getAttribute('data-less') || 'Read less') : (btn.getAttribute('data-more') || 'Read more');
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    }
  }

  /* 3. Attribution on intent */
  function decorate(a) {
    if (!a || a.dataset.lv3Decorated) return;
    if (!ctx.town || !ctx.initial) return;
    try {
      var u = new URL(a.getAttribute('href'), window.location.origin);
      u.searchParams.set('florist_city', ctx.town);
      u.searchParams.set('florist_state', ctx.initial);
      if (ctx.utmCampaign) u.searchParams.set('utm_campaign', ctx.utmCampaign);
      if (ctx.pageUrl) u.searchParams.set('utm_source', ctx.pageUrl);
      a.setAttribute('href', u.pathname + u.search);
      a.dataset.lv3Decorated = '1';
    } catch (e) { /* leave the clean href */ }
  }
  function onIntent(e) {
    var a = e.target && e.target.closest ? e.target.closest('[data-lv3-product-link]') : null;
    if (a) decorate(a);
  }
  d.addEventListener('pointerdown', onIntent, true);
  d.addEventListener('touchstart', onIntent, { capture: true, passive: true });
  d.addEventListener('focusin', onIntent, true);
  d.addEventListener('keydown', function (e) {
    if (e.key === 'Enter' || e.key === ' ') onIntent(e);
  }, true);

  /* 2. Tabs: one tab set per product block (gift baskets and, where enabled, flowers), each with its own panel, cache and
     fragment URLs. The default panel is cached with its button row (Show more, Browse all) so it comes back whole. */
  function initTabs(root) {
    var panel = root.querySelector('[data-lv3-panel]');
    var loading = root.querySelector('[data-lv3-loading]');
    var tabs = Array.prototype.slice.call(root.querySelectorAll('[data-lv3-tab]'));
    if (!panel) return;
    var cache = {};
    var defaultParts = panel.querySelectorAll('[data-lv3-grid], .lv3-more');
    if (defaultParts.length) cache['default'] = Array.prototype.map.call(defaultParts, function (n) { return n.outerHTML; }).join('');

    /* 6. Show more (29 Sep 2026, Nick: nothing loads before the button is pressed). A tap puts card-shaped placeholders in the
       grid straight away, so the page grows only in answer to the tap, then fetches the next batch and swaps each placeholder
       for its card in place (a card is exactly as tall as its placeholder, so nothing moves when they land). The button always
       says how many the next tap shows and goes when the category has nothing more.
       Most sent grid: data-lv3-queue lists the next products as handle@fragment-URL, so exactly those are fetched.
       Tab and chip grids: data-lv3-next is the next page of the collection fragment, data-lv3-total its available products. */
    function curGrid() { return panel.querySelector('[data-lv3-grid]'); }
    function stepOf(b) { var n = parseInt(b.getAttribute('data-lv3-step'), 10); return n > 0 ? n : 20; }
    function realCards(g) { return g ? g.querySelectorAll('[data-lv3-card]') : []; }
    function queueOf(g) { var q = g ? g.getAttribute('data-lv3-queue') : ''; return q ? q.split('|').filter(Boolean) : []; }
    function leftIn(g) {
      if (!g) return 0;
      if (g.hasAttribute('data-lv3-queue')) return queueOf(g).length;
      if (!g.getAttribute('data-lv3-next')) return 0;
      var total = parseInt(g.getAttribute('data-lv3-total'), 10);
      var left = total - realCards(g).length;
      return left > 0 ? left : 1;
    }
    function refresh() {
      var g = curGrid();
      Array.prototype.forEach.call(panel.querySelectorAll('[data-lv3-more]'), function (b) {
        if (b.getAttribute('aria-busy') === 'true') return;
        var left = leftIn(g);
        if (left <= 0) { if (b.parentNode) b.parentNode.removeChild(b); return; }
        var tpl = b.getAttribute('data-lv3-label') || 'Show {n} more';
        b.textContent = tpl.replace('{n}', Math.min(stepOf(b), left));
        b.hidden = false;
      });
    }
    function handleOf(li) {
      var a = li.querySelector('[data-lv3-product-link]');
      var h = a ? (a.getAttribute('href') || '').split('?')[0] : '';
      return h.slice(h.lastIndexOf('/') + 1);
    }
    function placeholders(g, n) {
      var model = g.querySelector('[data-lv3-card]');
      var out = [];
      for (var i = 0; i < n && model; i++) {
        var s = model.cloneNode(true);
        s.removeAttribute('data-lv3-card');
        s.setAttribute('data-lv3-skel', '');
        s.setAttribute('aria-hidden', 'true');
        s.classList.add('lv3-card--skel');
        Array.prototype.forEach.call(s.querySelectorAll('img'), function (im) { im.removeAttribute('srcset'); im.removeAttribute('src'); });
        Array.prototype.forEach.call(s.querySelectorAll('a'), function (a) { a.removeAttribute('href'); a.removeAttribute('data-lv3-product-link'); a.setAttribute('tabindex', '-1'); });
        g.appendChild(s);
        out.push(s);
      }
      return out;
    }
    function cardsFrom(html) {
      var tmp = d.createElement('div');
      tmp.innerHTML = html;
      var src = tmp.querySelector('[data-lv3-grid]');
      return { grid: src, cards: src ? Array.prototype.slice.call(src.querySelectorAll('[data-lv3-card]')) : [] };
    }
    function get(url) {
      return fetch(url, { credentials: 'same-origin' }).then(function (r) { return r.ok ? r.text() : Promise.reject(r.status); });
    }
    function land(g, skels, cards) {
      var have = {};
      Array.prototype.forEach.call(realCards(g), function (li) { have[handleOf(li)] = 1; });
      var k = 0;
      cards.forEach(function (li) {
        var h = handleOf(li);
        if (!h || have[h]) return;
        have[h] = 1;
        li.hidden = false;
        li.removeAttribute('data-lv3-extra');
        li.classList.remove('lv3-card--extra');
        if (k < skels.length) g.replaceChild(li, skels[k]); else g.appendChild(li);
        k++;
      });
      for (; k < skels.length; k++) if (skels[k].parentNode) skels[k].parentNode.removeChild(skels[k]);
    }
    panel.addEventListener('click', function (e) {
      var b = e.target && e.target.closest ? e.target.closest('[data-lv3-more]') : null;
      if (!b) return;
      e.preventDefault();
      if (b.getAttribute('aria-busy') === 'true') return;
      var g = curGrid();
      var left = leftIn(g), step = stepOf(b);
      var n = Math.min(step, left);
      if (!g || n <= 0) return;
      var after = left - n;
      var skels = placeholders(g, n);
      var job;
      if (g.hasAttribute('data-lv3-queue')) {
        var q = queueOf(g), batch = q.slice(0, n), rest = q.slice(n);
        if (rest.length) g.setAttribute('data-lv3-queue', rest.join('|')); else g.setAttribute('data-lv3-queue', '');
        var urls = [];
        batch.forEach(function (it) { var u = it.slice(it.indexOf('@') + 1); if (urls.indexOf(u) < 0) urls.push(u); });
        job = Promise.all(urls.map(get)).then(function (pages) {
          var byHandle = {};
          pages.forEach(function (html) { cardsFrom(html).cards.forEach(function (li) { byHandle[handleOf(li)] = li; }); });
          var ordered = [];
          batch.forEach(function (it) { var li = byHandle[it.slice(0, it.indexOf('@'))]; if (li) ordered.push(li); });
          if (curGrid() === g) land(g, skels, ordered);
        });
      } else {
        var next = g.getAttribute('data-lv3-next');
        job = get(next).then(function (html) {
          var got = cardsFrom(html);
          if (curGrid() !== g) return;
          var nx = got.grid && got.grid.getAttribute('data-lv3-next');
          if (nx) g.setAttribute('data-lv3-next', nx); else g.removeAttribute('data-lv3-next');
          land(g, skels, got.cards);
        });
      }
      /* the label for the tap after this one, or the button goes if this batch is the last */
      b.setAttribute('aria-busy', 'true');
      b.disabled = true;
      if (after <= 0) b.hidden = true;
      else b.textContent = (b.getAttribute('data-lv3-label') || 'Show {n} more').replace('{n}', Math.min(step, after));
      job.catch(function () {
        skels.forEach(function (s) { if (s.parentNode) s.parentNode.removeChild(s); });
        window.location.href = (panel.querySelector('.lv3-more > a') || {}).href || window.location.href;
      }).then(function () {
        b.removeAttribute('aria-busy');
        b.disabled = false;
        refresh();
      });
    });
    function armMore() { refresh(); }
    armMore();
    if (!tabs.length) return;

    function setActive(tab) {
      tabs.forEach(function (t) {
        var on = t === tab;
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.setAttribute('tabindex', on ? '0' : '-1');
      });
      panel.setAttribute('aria-labelledby', tab.id);
      if (tab.scrollIntoView) { try { tab.scrollIntoView({ block: 'nearest', inline: 'nearest' }); } catch (e) { /* older engines */ } }
    }
    function render(html) {
      var old = panel.querySelectorAll('[data-lv3-grid], .lv3-more');
      Array.prototype.forEach.call(old, function (n) { n.parentNode.removeChild(n); });
      var tmp = d.createElement('div');
      tmp.innerHTML = html;
      var frag = d.createDocumentFragment();
      Array.prototype.slice.call(tmp.querySelectorAll('[data-lv3-grid], .lv3-more')).forEach(function (n) { frag.appendChild(n); });
      panel.insertBefore(frag, loading || null);
      armMore();
    }
    var want = 'default';
    function load(key, url) {
      want = key;
      if (cache[key]) { render(cache[key]); return; }
      if (!url) return;
      if (loading) loading.hidden = false;
      fetch(url, { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.text() : Promise.reject(r.status); })
        .then(function (html) {
          cache[key] = html;
          if (loading) loading.hidden = true;
          if (want === key) render(html);
        })
        .catch(function () {
          if (loading) loading.hidden = true;
          window.location.href = url.split('?')[0];
        });
    }

    /* 5. Sub-category chips for one tab */
    var subsTpl = root.querySelector('template[data-lv3-subs-for]');
    var subsFor = subsTpl && subsTpl.content ? subsTpl.getAttribute('data-lv3-subs-for') : null;
    var subsNav = null;
    function markChip(active) {
      Array.prototype.forEach.call(subsNav.querySelectorAll('[data-lv3-sub]'), function (c) {
        var on = c === active;
        c.classList.toggle('is-active', on);
        if (on) c.setAttribute('aria-current', 'true'); else c.removeAttribute('aria-current');
      });
    }
    function setSubs(key) {
      if (!subsFor) return;
      if (key !== subsFor) {
        if (subsNav && subsNav.parentNode) subsNav.parentNode.removeChild(subsNav);
        return;
      }
      if (!subsNav) {
        subsNav = subsTpl.content.firstElementChild.cloneNode(true);
        subsNav.addEventListener('click', function (e) {
          var a = e.target && e.target.closest ? e.target.closest('[data-lv3-sub]') : null;
          if (!a) return;
          e.preventDefault();
          markChip(a);
          load(a.getAttribute('data-lv3-sub'), a.getAttribute('data-lv3-tab-url'));
          if (a.scrollIntoView) { try { a.scrollIntoView({ block: 'nearest', inline: 'nearest' }); } catch (e2) { /* older engines */ } }
        });
      }
      markChip(subsNav.querySelector('[data-lv3-sub]'));
      if (!subsNav.parentNode) panel.insertBefore(subsNav, panel.firstChild);
    }

    function show(tab) {
      var key = tab.getAttribute('data-lv3-tab');
      setActive(tab);
      setSubs(key);
      load(key, tab.getAttribute('data-lv3-tab-url'));
    }
    tabs.forEach(function (t, i) {
      t.addEventListener('click', function () { show(t); });
      t.addEventListener('keydown', function (e) {
        var j = i;
        if (e.key === 'ArrowRight') j = (i + 1) % tabs.length;
        else if (e.key === 'ArrowLeft') j = (i - 1 + tabs.length) % tabs.length;
        else return;
        e.preventDefault();
        tabs[j].focus();
        show(tabs[j]);
      });
    });
  }
  Array.prototype.forEach.call(d.querySelectorAll('[data-lv3-products]'), function (root) {
    if (root.dataset.lv3TabsInit) return;
    root.dataset.lv3TabsInit = '1';
    initTabs(root);
  });

  /* 4. Map facade */
  var map = d.querySelector('[data-lv3-map]');
  if (map) {
    var open = map.querySelector('[data-lv3-map-open]');
    var src = map.getAttribute('data-lv3-map-src');
    if (open && src) {
      open.addEventListener('click', function () {
        var iframe = d.createElement('iframe');
        iframe.src = src;
        iframe.title = map.getAttribute('data-lv3-map-title') || 'Map';
        iframe.setAttribute('allowfullscreen', '');
        var facade = map.querySelector('[data-lv3-map-facade]');
        if (facade) facade.parentNode.removeChild(facade);
        map.appendChild(iframe);
      });
    }
  }
})();
